import { useEffect, useRef, useState } from 'react';
import { supabaseClient } from '../../config/supabase';
import { PHONE_COUNTRIES, staffPhone } from '../../lib/staffPhone';
import { formatPhone } from '../../lib/phone';
import { STAFF_APPS, provisionStaffApps, staffAppSelection } from '../../lib/staffApps';
import HelpTip from '../ui/HelpTip';
import { SecondaryPlacementEditor } from './StaffPlacements';
import ReportsToField, { reportsToName, reportsToOptions } from './ReportsToField';
import ModalSurface from '../shared/ModalSurface';
import './staffUserForm.css';
import { ENTITY_CODE_PATTERN, ENTITY_CODE_RULE, normalizeEntityCode, suggestEntityCode } from '../../lib/entityCode';

// UX pass 2026-10-25: the server refuses a New Staff save when the home Company/Market has no code. The form now finds that
// before Save and offers to set the code right here (Owner/Admin; the database decides who may change it).
export function missingCodeEntity(entities, homeId, overrides = {}) {
    const home = entities.find((item) => item.id === homeId);
    if (!home || !Object.prototype.hasOwnProperty.call(home, 'entity_code')) return null;
    const code = overrides[home.id] ?? home.entity_code;
    return code && ENTITY_CODE_PATTERN.test(code) ? null : home;
}

function CompanyCodePrompt({ entity, entities, onSet, client = supabaseClient }) {
    const [code, setCode] = useState(() => suggestEntityCode(entity.name, entities.map((item) => item.entity_code).filter(Boolean)));
    const [busy, setBusy] = useState(false);
    const [problem, setProblem] = useState('');
    async function saveCode() {
        const value = normalizeEntityCode(code);
        if (!ENTITY_CODE_PATTERN.test(value)) { setProblem(ENTITY_CODE_RULE); return; }
        if (entities.some((item) => item.id !== entity.id && item.entity_code === value)) { setProblem('This code is already used in your organization.'); return; }
        setBusy(true); setProblem('');
        const { data, error } = await client.from('entities').update({ entity_code: value }).eq('id', entity.id).select('id, entity_code');
        setBusy(false);
        if (error) { setProblem(`The code could not be saved: ${error.message}`); return; }
        if (Array.isArray(data) && data.length === 0) { setProblem('Only an Owner or Admin can set this code. Ask them to set it in Admin Center › Staff & Roles › Structure.'); return; }
        onSet(entity.id, value);
    }
    return <div role="alert" data-testid="company-code-prompt" className="md:col-span-2 rounded-lg border border-[var(--ui-moderate,#FB9846)] p-3 text-sm">
        <p className="font-medium">{entity.name} needs a short code before you can save staff here.</p>
        <p className="mt-1">The code starts every employee ID and confirmation number (for example ATL). {ENTITY_CODE_RULE}</p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
            <label htmlFor="staff-company-code" className="font-medium">Code</label>
            <input id="staff-company-code" value={code} maxLength={5} disabled={busy} onChange={(event) => setCode(normalizeEntityCode(event.target.value))}
                className={`${INPUT} w-28 font-mono uppercase`} />
            <button type="button" className={BUTTON} disabled={busy} onClick={saveCode}>{busy ? 'Saving…' : 'Save code'}</button>
        </div>
        {problem && <p className="mt-2 text-red-600">{problem}</p>}
    </div>;
}

const HELP = {
    Company: 'The business this person works for (e.g., The Ride).',
    Market: 'The branch or region within that company (e.g., ATL).',
    Department: 'The function they report up through (e.g., Operations, HR).',
    Title: 'Their job title (e.g., VP Marketing).',
    'Employment Type': 'W-2 employee or 1099 contractor. Controls pay and tax handling.',
    'Role Tag': 'What they lead or own beyond their title (e.g., Team Lead). Managed under Structure.',
    Status: 'Whether this person is active, on leave, inactive, or terminated on the roster.'
};
const INPUT = 'w-full rounded-lg border border-[var(--surface-border)] bg-[var(--surface-sunken)] px-3 py-2 text-[var(--ink)]';
const BUTTON = 'ia-hover-pulse rounded-full border border-[var(--surface-border)] px-4 py-2 disabled:opacity-50';

function Field({ label, children, error, errorId }) {
    return <label className="block text-sm"><span className="mb-2 block font-medium">{label}
        {HELP[label] && <HelpTip label={label}>{HELP[label]}</HelpTip>}</span>{children}
        {error && <span id={errorId} className="mt-1 block text-xs font-medium text-red-600" role="alert">{error}</span>}</label>;
}

export default function StaffUserForm({ staff = null, person = null, entities, groups, roleTags,
    rolePresets = [], stepDefinitions, canEditTarget, canPlaceOrganization = false, onUpdateTripPriceVisibility,
    onClose, onSaved, onOpenPerson, staffDirectory = [] }) {
    const contact = staff?.person_id || staff?.user_profile_id ? staff : person;
    const isOwner = contact?.command_center_role === 'owner';
    const needsDriverOnboarding = !staff?.driver_id
        || (!staff?.applicant_id && !contact?.user_profile_id);
    const placement = entities.find((item) => item.id === staff?.entity_id);
    const organizationHomeLocked = Boolean(staff && placement?.level_type === 'organization' && !canPlaceOrganization);
    const [form, setForm] = useState({
        first_name: contact?.full_name?.split(' ')[0] || '',
        last_name: contact?.full_name?.split(' ').slice(1).join(' ') || '',
        email: contact?.email || '', mobile: formatPhone(contact?.phone) || '',
        company_id: ['company', 'organization'].includes(placement?.level_type) ? placement.id : placement?.parent_entity_id || '',
        entity_id: staff?.entity_id || '', group_id: staff?.group_id || '',
        secondary_entity_ids: staff?.secondary_entity_ids || [],
        title: staff?.title || '', employment_type: staff?.employment_type || '',
        role_tag_id: staff?.role_tag_id || '', status: staff?.status || 'active',
        reports_to_staff_id: staff?.reports_to_staff_id || ''
    });
    const [country, setCountry] = useState('');
    const [countryRequired, setCountryRequired] = useState(false);
    const [apps, setApps] = useState(() => staffAppSelection(staff || person || {}));
    const [step, setStep] = useState(1);
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const [pendingFocus, setPendingFocus] = useState('');
    const [duplicate, setDuplicate] = useState(null);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(null);
    const [updatingPriceVisibility, setUpdatingPriceVisibility] = useState(false);
    const [codeOverrides, setCodeOverrides] = useState({});
    const saveErrorRef = useRef(null);
    const contactRevision = useRef(0);
    const heading = useRef(null);
    const headingFocusTimeout = useRef(null);
    const fieldRefs = useRef({});
    const phone = staffPhone(form.mobile, country);
    const contactValues = useRef({});
    contactValues.current = { email: form.email, mobile: phone.e164 || '' };
    const companies = entities.filter((item) => item.level_type === 'company'
        || (item.level_type === 'organization' && canPlaceOrganization)
        || (organizationHomeLocked && item.id === placement.id));
    const markets = organizationHomeLocked ? []
        : entities.filter((item) => item.level_type === 'market' && item.parent_entity_id === form.company_id);
    const departments = groups.filter((item) => item.entity_id === form.entity_id);
    const homeIdForCode = organizationHomeLocked ? staff?.entity_id : form.entity_id || form.company_id;
    const codeless = missingCodeEntity(entities, homeIdForCode, codeOverrides);
    // Reports to (migration 259): an existing record carries the database's answer; a new one follows Personnel edit scope.
    const canChangeReportsTo = staff ? staff.reports_to_can_change === true
        : Boolean(canEditTarget?.(form.entity_id || form.company_id, form.group_id || null));
    // A refusal from the server on Save shows beside the Save button (step 3 is long; the top of the dialog is out of view).
    useEffect(() => {
        if (!error || step !== 3) return;
        const node = saveErrorRef.current;
        node?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
        node?.focus?.();
    }, [error, step]);
    const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));
    const setApp = (key, values) => setApps((current) => ({ ...current, [key]: { ...current[key], ...values } }));
    useEffect(() => {
        if (!pendingFocus) return;
        fieldRefs.current[pendingFocus]?.focus();
        setPendingFocus('');
    }, [pendingFocus, step]);

    const fieldId = (key) => `staff-field-${key.replace(/[^a-z0-9-]/gi, '-')}`;
    const fieldProps = (key) => ({
        id: fieldId(key),
        ref: (node) => { fieldRefs.current[key] = node; },
        'aria-invalid': Boolean(fieldErrors[key]),
        'aria-describedby': fieldErrors[key] ? `${fieldId(key)}-error` : undefined,
        className: `${INPUT}${fieldErrors[key] ? ' border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`,
        style: fieldErrors[key] ? { borderColor: '#ef4444', outline: '2px solid #ef4444', outlineOffset: '1px' } : undefined,
    });
    const fieldError = (key) => fieldErrors[key]
        ? <span id={`${fieldId(key)}-error`} className="mt-1 block text-xs font-medium text-red-600" role="alert">{fieldErrors[key]}</span>
        : null;
    const clearFieldError = (key) => {
        setFieldErrors((current) => {
            if (!current[key]) return current;
            const nextErrors = { ...current };
            delete nextErrors[key];
            return nextErrors;
        });
        if (pendingFocus === key) setPendingFocus('');
    };
    const showValidation = (errors) => {
        clearTimeout(headingFocusTimeout.current);
        setError('');
        setFieldErrors(errors);
        const firstField = Object.keys(errors)[0];
        if (!firstField) return false;
        setPendingFocus(firstField);
        if (firstField === 'employment_type' || (firstField === 'title' && step === 2)) setStep(2);
        else if (['company_id', 'entity_id', 'group_id'].includes(firstField)) setStep(2);
        else if (firstField.startsWith('apps.')) setStep(3);
        else setStep(1);
        return true;
    };
    const go = (value) => {
        clearTimeout(headingFocusTimeout.current);
        setError(''); setFieldErrors({}); setStep(value);
        headingFocusTimeout.current = setTimeout(() => heading.current?.focus(), 0);
    };

    async function checkDuplicate() {
        if (contact) return null;
        while (true) {
            const revision = contactRevision.current;
            const values = contactValues.current;
            const { data, error: lookupError } = await supabaseClient.rpc('bl391_find_person', {
                p_email: values.email.trim().toLowerCase(), p_mobile: values.mobile
            });
            if (revision !== contactRevision.current) continue;
            if (lookupError) throw new Error(`Could not check for duplicates: ${lookupError.message}`);
            const found = data?.[0] || null;
            setDuplicate(found);
            return found;
        }
    }

    async function next() {
        setError('');
        setFieldErrors({});
        if (step === 1 && !contact) {
            const errors = {};
            if (!form.first_name.trim()) errors.first_name = 'First name is required.';
            if (!form.last_name.trim()) errors.last_name = 'Last name is required.';
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errors.email = 'Enter a valid email address.';
            if (!phone.country) {
                errors.mobile_country = 'Choose a country for this mobile number.';
                setCountryRequired(true);
            } else if (!phone.e164) errors.mobile = 'Enter a valid mobile number for this country.';
            if (showValidation(errors)) return;
            try {
                setSaving(true);
                if (await checkDuplicate() !== null) return;
            } catch (e) { setError(e.message); return; }
            finally { setSaving(false); }
        }
        if (step === 2) {
            const errors = {};
            if (!form.company_id) errors.company_id = 'Choose a Company.';
            if (!form.title.trim()) errors.title = 'Title is required.';
            if (form.company_id && codeless) errors.company_id = `Set a code for ${codeless.name} first (just below).`;
            if (form.entity_id && !canEditTarget(form.entity_id, form.group_id || null)) {
                const hasAssignableDepartment = groups.some((group) =>
                    group.entity_id === form.entity_id && canEditTarget(form.entity_id, group.id));
                const targetField = form.group_id ? 'group_id' : hasAssignableDepartment ? 'group_id'
                    : form.entity_id !== form.company_id ? 'entity_id' : 'company_id';
                errors[targetField] = 'Choose a placement within your Personnel edit scope.';
            }
            if (showValidation(errors)) return;
        }
        go(step + 1);
    }

    async function save() {
        setError('');
        const errors = {};
        const homeEntityId = organizationHomeLocked ? staff.entity_id : form.entity_id || form.company_id;
        if (!homeEntityId) errors.company_id = 'Choose a Company.';
        else if (codeless) errors.company_id = `Set a code for ${codeless.name} first (just below).`;
        if (apps.command_center.enabled && apps.command_center.role !== 'admin' && !apps.command_center.entity_ids.length)
            errors['apps.command_center.entity_ids'] = 'Choose at least one Company/Market.';
        if (apps.driver_app.enabled && needsDriverOnboarding) {
            if (!form.employment_type) errors.employment_type = 'Driver App requires an Employment Type.';
            if (apps.driver_app.onboarding_mode === 'guided' && !apps.driver_app.step_ids.length)
                errors['apps.driver_app.step_ids'] = 'Choose at least one Guided onboarding step.';
            if (apps.driver_app.onboarding_mode === 'manual' && !apps.driver_app.attestation)
                errors['apps.driver_app.attestation'] = 'Verify the offline documents.';
            if (apps.driver_app.onboarding_mode === 'manual' && !apps.driver_app.note.trim())
                errors['apps.driver_app.note'] = 'Enter a Manual onboarding note.';
        }
        if (showValidation(errors)) return;
        try {
            setSaving(true);
            let result = saved;
            const previousCommandCenterRole = staff?.command_center_role || person?.command_center_role;
            if (!result) {
                if (!contact && await checkDuplicate() !== null) { go(1); return; }
                const appAccess = (apps.command_center.enabled && apps.command_center.role === 'user')
                    || previousCommandCenterRole === 'user'
                    ? { ...apps, command_center: undefined } : apps;
                const { data, error: saveError } = await supabaseClient.rpc('bl391_save_staff', {
                    p_staff_id: staff?.id || null,
                    p_expected_person: staff?.person_id || staff?.user_profile_id || null,
                    p_record: {
                        entity_id: homeEntityId, group_id: form.group_id || null,
                        secondary_entity_ids: form.secondary_entity_ids.filter((id) => id !== homeEntityId),
                        title: form.title.trim(), employment_type: form.employment_type || null,
                        role_tag_id: form.role_tag_id || null, status: form.status,
                        person_id: contact?.person_id || null,
                        user_profile_id: contact?.person_id ? null : contact?.user_profile_id || null,
                        new_person: contact ? null : {
                            first_name: form.first_name.trim(), last_name: form.last_name.trim(),
                            email: form.email.trim().toLowerCase(), mobile: phone.e164
                        }
                    },
                    p_app_access: appAccess
                });
                if (saveError) throw saveError;
                if (!data?.staff_id) throw new Error('The staff record was not saved.');
                result = data;
                setSaved(result);
            }
            if (apps.command_center.role === 'user' || previousCommandCenterRole === 'user') {
                const { error: accessError } = await supabaseClient.rpc('bl395a_set_staff_user_access', {
                    p_staff_id: result.staff_id,
                    p_enabled: apps.command_center.enabled,
                    p_preset_id: apps.command_center.enabled ? apps.command_center.preset_id : null,
                    p_entity_ids: apps.command_center.enabled ? apps.command_center.entity_ids : [],
                    p_role: apps.command_center.role,
                });
                if (accessError) throw accessError;
            }
            if (canChangeReportsTo && (form.reports_to_staff_id || null) !== (staff?.reports_to_staff_id || null)) {
                const { error: reportsToError } = await supabaseClient.rpc('staff_set_reports_to', {
                    p_staff_id: result.staff_id, p_reports_to_staff_id: form.reports_to_staff_id || null,
                });
                if (reportsToError) throw new Error(`Reports to was not saved: ${reportsToError.message}`);
            }
            await provisionStaffApps(apps, result, { email: form.email.trim().toLowerCase() });
            await onSaved();
        } catch (e) { setError(e.message || 'Staff could not be saved.'); }
        finally { setSaving(false); }
    }

    return <ModalSurface label="Staff/User" labelledBy="staff-user-title" onClose={onClose} closeOnEscape={false} closeDisabled={saving} busy={saving}
        overlayClassName="fixed inset-0 z-50 flex items-center justify-center bg-[var(--surface-canvas)]/90 p-4"
        panelClassName="ia-staff-user-form max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl border border-[var(--surface-border)] bg-[var(--surface-raised)] p-6 text-[var(--ink)]">
        <div className="ia-modal-layout ia-staff-user-layout">
          <div className="ia-modal-body">
            <h2 id="staff-user-title" className="text-xl font-semibold">{staff ? 'Edit Staff/User' : 'New Staff/User'}</h2>
            <h3 ref={heading} tabIndex={-1} className="my-4 font-semibold">Step {step}: {['Person', 'Placement', 'App Access'][step - 1]}</h3>
            {error && <p role={step === 3 ? undefined : 'alert'} className="my-3 text-red-600">{error}</p>}
            {saved && <p role="status">Staff record saved. Retry app access without creating another person.</p>}
            {duplicate && <div role="alert" className="my-3">
                <p>This person already exists — open their record?</p>
                <button className={BUTTON} onClick={() => onOpenPerson(duplicate)}>Open their record</button>
            </div>}
            <fieldset disabled={saving || Boolean(saved)} className="space-y-4">
                {step === 1 && <div className="grid gap-4 md:grid-cols-2">
                    {[['first_name', 'First name'], ['last_name', 'Last name'], ['email', 'Email'], ['mobile', 'Mobile']].map(([key, label]) =>
                        <Field key={key} label={label} error={fieldErrors[key]} errorId={`${fieldId(key)}-error`}><input {...fieldProps(key)}
                            type={key === 'email' ? 'email' : key === 'mobile' ? 'tel' : 'text'}
                            value={form[key]} readOnly={Boolean(contact)}
                            onChange={(event) => {
                                contactRevision.current += 1; setDuplicate(null); setError(''); clearFieldError(key);
                                if (key === 'mobile') {
                                    const value = staffPhone(event.target.value, country);
                                    set(key, value.formatted);
                                    setCountryRequired(!value.country);
                                } else set(key, event.target.value);
                            }}
                            onBlur={['email', 'mobile'].includes(key) ? async () => {
                                try { await checkDuplicate(); } catch (e) { setError(e.message); }
                            } : undefined} /></Field>)}
                    {!contact && (countryRequired || country || fieldErrors.mobile_country) && <Field label="Mobile country" error={fieldErrors.mobile_country} errorId={`${fieldId('mobile_country')}-error`}>
                        <select {...fieldProps('mobile_country')} value={country} onChange={(event) => {
                            contactRevision.current += 1; setDuplicate(null);
                            clearFieldError('mobile_country'); clearFieldError('mobile');
                            setCountry(event.target.value); setCountryRequired(false);
                            set('mobile', staffPhone(form.mobile, event.target.value).formatted);
                        }}><option value="">Choose a country</option>
                            {PHONE_COUNTRIES.map((item) => <option key={item.code} value={item.code}>{item.name} (+{item.callingCode})</option>)}
                        </select>
                    </Field>}
                    {contact && <p className="text-sm">Existing person and login retained. This form changes placement and app access, not their identity.</p>}
                </div>}
                {step === 2 && <div className="grid gap-4 md:grid-cols-2">
                    <Field label="Company" error={fieldErrors.company_id} errorId={`${fieldId('company_id')}-error`}><select {...fieldProps('company_id')} value={form.company_id} disabled={organizationHomeLocked} onChange={(event) => {
                        clearFieldError('company_id'); clearFieldError('entity_id'); clearFieldError('group_id');
                        setForm((current) => ({ ...current, company_id: event.target.value, entity_id: event.target.value, group_id: '' }));
                    }}>
                        <option value="">Choose a Company</option>{companies.map((item) => <option key={item.id} value={item.id}>{item.name}{item.level_type === 'organization' ? ' (Organization)' : ''}</option>)}
                    </select>{organizationHomeLocked && <p className="mt-1 text-xs">Organization placement — Owner/Super Admin can change</p>}</Field>
                    {codeless && <CompanyCodePrompt key={codeless.id} entity={codeless} entities={entities}
                        onSet={(id, value) => { setCodeOverrides((current) => ({ ...current, [id]: value })); clearFieldError('company_id'); }} />}
                    <div className="md:col-span-2">
                        <p className="mb-2 text-xs">Home placement drives HR/payroll and the initial employee ID prefix, not access.</p>
                        <SecondaryPlacementEditor entities={entities} homeId={form.entity_id}
                            value={form.secondary_entity_ids} canEditTarget={canEditTarget}
                            onChange={(value) => set('secondary_entity_ids', value)} />
                    </div>
                    <Field label="Market" error={fieldErrors.entity_id} errorId={`${fieldId('entity_id')}-error`}><select {...fieldProps('entity_id')} value={form.entity_id === form.company_id ? '' : form.entity_id}
                        disabled={!form.company_id || organizationHomeLocked} onChange={(event) => {
                            clearFieldError('entity_id'); clearFieldError('group_id');
                            setForm((current) => ({ ...current, entity_id: event.target.value || current.company_id, group_id: '' }));
                        }}>
                        <option value="">Company-wide (no Market)</option>{markets.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                    </select></Field>
                    <Field label="Department" error={fieldErrors.group_id} errorId={`${fieldId('group_id')}-error`}><select {...fieldProps('group_id')} value={form.group_id} onChange={(event) => { clearFieldError('group_id'); set('group_id', event.target.value); }}>
                        <option value="">No Department</option>{departments.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                    </select></Field>
                    <div className="md:col-span-2"><ReportsToField value={form.reports_to_staff_id}
                        onChange={(value) => set('reports_to_staff_id', value)} canChange={canChangeReportsTo}
                        options={reportsToOptions(staffDirectory, staff?.id)}
                        managerName={reportsToName(staffDirectory, form.reports_to_staff_id)} /></div>
                    <Field label="Title" error={fieldErrors.title} errorId={`${fieldId('title')}-error`}><input {...fieldProps('title')} value={form.title} onChange={(event) => { clearFieldError('title'); set('title', event.target.value); }} /></Field>
                    <Field label="Employment Type" error={fieldErrors.employment_type} errorId={`${fieldId('employment_type')}-error`}><select {...fieldProps('employment_type')} value={form.employment_type} onChange={(event) => { clearFieldError('employment_type'); set('employment_type', event.target.value); }}>
                        <option value="">Unclassified</option><option value="w2_staff">W-2 Employee</option><option value="1099_contractor">1099 Contractor</option>
                    </select></Field>
                    <Field label="Role Tag"><select className={INPUT} value={form.role_tag_id} onChange={(event) => set('role_tag_id', event.target.value)}>
                        <option value="">No Role Tag</option>{roleTags.filter((item) => item.active || item.id === form.role_tag_id).map((item) =>
                            <option key={item.id} value={item.id}>{item.name}{!item.active ? ' (Inactive)' : ''}</option>)}
                    </select></Field>
                    <Field label="Status"><select className={INPUT} value={form.status} onChange={(event) => set('status', event.target.value)}>
                        {['active', 'inactive', 'on_leave', 'terminated'].map((value) => <option key={value} value={value}>{value.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase())}</option>)}
                    </select></Field>
                </div>}
                {step === 3 && <>
                    <p>None selected = roster only, no login. One email and login are shared across apps.</p>
                    {STAFF_APPS.map((app) => <section key={app.id} className="rounded-lg border border-[var(--surface-border)] p-4">
                        <label><input type="checkbox" checked={apps[app.id].enabled} disabled={app.id === 'command_center' && isOwner}
                            onChange={(event) => {
                                if (app.id === 'command_center') clearFieldError('apps.command_center.entity_ids');
                                if (app.id === 'driver_app') {
                                    clearFieldError('apps.driver_app.step_ids');
                                    clearFieldError('apps.driver_app.attestation');
                                    clearFieldError('apps.driver_app.note');
                                    clearFieldError('employment_type');
                                }
                                setApp(app.id, { enabled: event.target.checked });
                            }} /> {app.label}</label>
                        <p className="text-sm text-[var(--ink-muted)]">{app.help}</p>
                        {apps[app.id].enabled && app.id === 'command_center' && <div className="mt-3 space-y-3">
                            {isOwner ? <p>Owner access is retained. Transfer ownership under App Access before changing this person's Central Command role.</p> :
                            <Field label="Central Command role"><select className={INPUT} value={apps.command_center.role}
                                onChange={(event) => { clearFieldError('apps.command_center.entity_ids'); clearFieldError('apps.command_center.preset_id');
                                    setApp(app.id, { role: event.target.value, preset_id: '', entity_ids: [] }); }}>
                                <option value="user">User</option><option value="admin">Admin</option><option value="assignable_admin">Assignable Admin</option>
                            </select></Field>}
                            {apps.command_center.role === 'admin' ? <p>Admin has organization-wide access under the existing role rules.</p> :
                            <>
                            {apps.command_center.role === 'user' && <Field label="Preset" error={fieldErrors['apps.command_center.preset_id']} errorId={`${fieldId('apps.command_center.preset_id')}-error`}>
                                <select {...fieldProps('apps.command_center.preset_id')} value={apps.command_center.preset_id}
                                    onChange={(event) => { clearFieldError('apps.command_center.preset_id'); setApp(app.id, { preset_id: event.target.value }); }}>
                                    <option value="">Inherit Team/Group default</option>{rolePresets.map((preset) =>
                                        <option key={preset.id} value={preset.id}>{preset.name}</option>)}
                                </select>
                            </Field>}
                            {apps.command_center.role === 'user' && apps.command_center.preset_id && (() => {
                                const selectedPreset = rolePresets.find((preset) => preset.id === apps.command_center.preset_id);
                                if (!selectedPreset) return null;
                                return <label className="flex items-start gap-2 text-sm">
                                    <input type="checkbox" checked={selectedPreset.show_trip_prices === true}
                                        disabled={updatingPriceVisibility || !onUpdateTripPriceVisibility}
                                        onChange={async (event) => {
                                            setError('');
                                            setUpdatingPriceVisibility(true);
                                            try {
                                                await onUpdateTripPriceVisibility(selectedPreset.id, event.target.checked);
                                            } catch (updateError) {
                                                setError(updateError.message || 'Trip-price visibility could not be updated.');
                                            } finally {
                                                setUpdatingPriceVisibility(false);
                                            }
                                        }} />
                                    <span>Show trip prices for everyone assigned to this preset</span>
                                </label>;
                            })()}
                            <fieldset ref={(node) => { fieldRefs.current['apps.command_center.entity_ids'] = node; }} tabIndex={-1}
                                className={fieldErrors['apps.command_center.entity_ids'] ? 'rounded border border-red-500 p-2' : ''}
                                style={fieldErrors['apps.command_center.entity_ids'] ? { borderColor: '#ef4444', outline: '2px solid #ef4444', outlineOffset: '1px' } : undefined}
                                aria-invalid={Boolean(fieldErrors['apps.command_center.entity_ids'])}>
                                <legend>Companies/Markets they can access</legend>{entities.filter((item) => item.level_type !== 'organization').map((item) =>
                                <label key={item.id} className="block"><input type="checkbox" checked={apps.command_center.entity_ids.includes(item.id)}
                                    onChange={(event) => {
                                        clearFieldError('apps.command_center.entity_ids');
                                        setApp(app.id, { entity_ids: event.target.checked
                                            ? [...apps.command_center.entity_ids, item.id]
                                            : apps.command_center.entity_ids.filter((id) => id !== item.id) });
                                    }} /> {item.name}</label>)}</fieldset>}
                                {fieldError('apps.command_center.entity_ids')}
                            </>
                            }
                            <p className="text-sm">Placement determines where they work, not what they can access. Invites use the existing team-invite path.</p>
                        </div>}
                        {apps[app.id].enabled && app.id === 'driver_app' && (!needsDriverOnboarding ? <p>Existing driver and onboarding history retained.</p> : <div className="mt-3 space-y-3">
                            {staff?.driver_id && <p>Existing driver status and history stay unchanged. Select Guided onboarding steps to recover their missing app login.</p>}
                            <Field label="Driver onboarding"><select className={INPUT} value={apps.driver_app.onboarding_mode} onChange={(event) => {
                                clearFieldError('apps.driver_app.step_ids');
                                clearFieldError('apps.driver_app.attestation');
                                clearFieldError('apps.driver_app.note');
                                setApp(app.id, { onboarding_mode: event.target.value });
                            }}>
                                <option value="guided">Guided</option>{!staff?.driver_id && <option value="manual">Manual</option>}
                            </select></Field>
                            {apps.driver_app.onboarding_mode === 'guided' ? <fieldset ref={(node) => { fieldRefs.current['apps.driver_app.step_ids'] = node; }} tabIndex={-1}
                                className={fieldErrors['apps.driver_app.step_ids'] ? 'rounded border border-red-500 p-2' : ''}
                                style={fieldErrors['apps.driver_app.step_ids'] ? { borderColor: '#ef4444', outline: '2px solid #ef4444', outlineOffset: '1px' } : undefined}
                                aria-invalid={Boolean(fieldErrors['apps.driver_app.step_ids'])}><legend>Guided onboarding steps</legend>
                                {stepDefinitions.map((item) => <label className="block" key={item.id}><input type="checkbox" checked={apps.driver_app.step_ids.includes(item.id)}
                                    onChange={(event) => { clearFieldError('apps.driver_app.step_ids'); setApp(app.id, { step_ids: event.target.checked ? [...apps.driver_app.step_ids, item.id] : apps.driver_app.step_ids.filter((id) => id !== item.id) }); }} /> {item.label}</label>)}
                            </fieldset> : <>
                                <label className={`block ${fieldErrors['apps.driver_app.attestation'] ? 'text-red-600' : ''}`}><input ref={(node) => { fieldRefs.current['apps.driver_app.attestation'] = node; }}
                                    style={fieldErrors['apps.driver_app.attestation'] ? { outline: '2px solid #ef4444', outlineOffset: '1px' } : undefined}
                                    type="checkbox" checked={apps.driver_app.attestation} onChange={(event) => { clearFieldError('apps.driver_app.attestation'); setApp(app.id, { attestation: event.target.checked }); }} /> I verified the offline documents.</label>
                                {fieldError('apps.driver_app.attestation')}
                                <Field label="Manual onboarding note" error={fieldErrors['apps.driver_app.note']} errorId={`${fieldId('apps.driver_app.note')}-error`}><textarea {...fieldProps('apps.driver_app.note')}
                                    value={apps.driver_app.note} onChange={(event) => { clearFieldError('apps.driver_app.note'); setApp(app.id, { note: event.target.value }); }} /></Field>
                            </>}
                            <p className="text-sm">Existing same-org logins are reused without resetting passwords. New users receive an invite, verify a one-time code, and create their own password.</p>
                        </div>)}
                    </section>)}
                </>}
            </fieldset>
          </div>
            {error && step === 3 && <p ref={saveErrorRef} tabIndex={-1} role="alert" data-testid="staff-save-error"
                className="mt-6 rounded-lg border border-red-500 p-3 text-sm font-medium text-red-600">Not saved: {error}</p>}
            <div className="ia-modal-actions mt-6 flex justify-end gap-3">
                <button className={BUTTON} disabled={saving} onClick={onClose}>{saved ? 'Close saved record' : 'Cancel'}</button>
                {step > 1 && !saved && <button className={BUTTON} disabled={saving} onClick={() => go(step - 1)}>Back</button>}
                {step < 3 ? <button className={BUTTON} disabled={saving || Boolean(duplicate)} onClick={next}>Next</button> :
                    <button className={BUTTON} disabled={saving} onClick={save}>{saving ? 'Saving...' : saved ? 'Retry app access' : 'Save Staff/User'}</button>}
            </div>
        </div>
    </ModalSurface>;
}
